import * as lodash from '../utils/lodash';
import { LegendType } from '../interface/legend';
import normalizeToArray from '../utils/normalizeToArray';
import { Pattern, LinearGradient, RadialGradient } from '../element';
import ColorScale from '../scale/color';
import LinearScale from '../scale/linear';
export function getValue(point, dataIndex, field) {
    if (field === '$index') {
        return dataIndex;
    }
    return point[field];
}
/**
 * default
 */
var Encode = /** @class */ (function () {
    function Encode(option, seriesModel) {
        this._uniqColorValues = [];
        this._option = option;
        this._seriesModel = seriesModel;
        this._initEncodeFunctions();
    }
    Encode.prototype.setOption = function (option) {
        this._option = option;
        this._initEncodeFunctions();
    };
    Encode.prototype.setDataset = function (dataset) {
        this._dataset = dataset;
    };
    Encode.prototype.setCodeMap = function (code, option) {
        if (code === 'color') {
            this._initColor(option);
        }
        else {
            this._initValueField(code, option);
        }
    };
    Encode.prototype.getDataset = function () {
        return this._dataset || this._seriesModel.getDataset();
    };
    Encode.prototype.getAxisField = function (axis) {
        var axisMap = {
            xAxis: 'x',
            yAxis: 'y',
            angleAxis: 'angle',
            radiusAxis: 'radius',
        };
        var shortAxis = axisMap[axis];
        return this._option[shortAxis];
    };
    Encode.prototype.getEncodeData = function (point, dataIndex) {
        var _this = this;
        var seriesModel = this._seriesModel;
        var _a = this._option, name = _a.name, value = _a.value, source = _a.source, target = _a.target, color = _a.color, size = _a.size, opacity = _a.opacity, shape = _a.shape;
        var ret = {};
        var axisFields = [
            'x',
            'y',
            'angle',
            'radius',
            'lng',
            'lat',
            'date',
        ];
        axisFields.forEach(function (axis) {
            var val = _this._option[axis];
            if (lodash.isString(val)) {
                ret[axis] = getValue(point, dataIndex, val);
            }
            else if (lodash.isNumber(val)) {
                ret[axis] = val;
            }
            else if (lodash.isArray(val)) {
                if (axis === 'lng' || axis === 'lat') {
                    var field = val[0];
                    var fildValue = getValue(point, dataIndex, field);
                    var fn = val[1];
                    ret[axis] = fn(fildValue, dataIndex, _this._seriesModel);
                }
                else {
                    ret[axis] = val.map(function (item) { return getValue(point, dataIndex, item); });
                }
            }
        });
        if (name) {
            ret.name = point[name];
        }
        if (value) {
            ret.value = point[value];
        }
        if (source) {
            ret.source = point[source];
        }
        if (target) {
            ret.target = point[target];
        }
        ret.color = this.color(getValue(point, dataIndex, normalizeToArray(color)[0]), dataIndex, seriesModel);
        if (!(lodash.isNull(size) || lodash.isUndefined(size))) {
            ret.size = this.size(getValue(point, dataIndex, size[0]), dataIndex, seriesModel);
        }
        if (opacity) {
            ret.opacity = this.opacity(getValue(point, dataIndex, opacity[0]), dataIndex, seriesModel);
        }
        if (shape) {
            ret.shape = this.shape(getValue(point, dataIndex, normalizeToArray(shape)[0]), dataIndex, seriesModel);
        }
        return ret;
    };
    Encode.prototype._initEncodeFunctions = function () {
        var _a = this._option, color = _a.color, size = _a.size, opacity = _a.opacity, shape = _a.shape;
        this._initColor(color);
        if (size || lodash.isNumber(size)) {
            this._initValueField('size', size);
        }
        if (opacity || lodash.isNumber(opacity)) {
            this._initValueField('opacity', opacity);
        }
        this._initShape(shape);
    };
    Encode.prototype._defaultColorEncoder = function (value, dataIndex, seriesModel) {
        var colors = seriesModel.chart.getOption().colors;
        if (seriesModel.legendType === LegendType.DATUM_LEGEND) {
            return colors[dataIndex % colors.length];
        }
        return seriesModel.getSeriesColor();
    };
    Encode.prototype._initColor = function (color) {
        var _this = this;
        if (!color) {
            this.color = this._defaultColorEncoder;
        }
        else if (lodash.isString(color) ||
            [LinearGradient, RadialGradient, Pattern].some(function (item) { return color instanceof item; })) {
            this.color = function () { return color; };
        }
        else if (lodash.isArray(color)) {
            var field = color[0];
            var arg2_1 = color[1]; // color array or color string or callback
            var scaleParam = color[2];
            if (lodash.isArray(arg2_1)) {
                this.color = function (value) {
                    _this._uniqColorValues = lodash.uniq(_this._uniqColorValues.concat(value));
                    var colorIndex = _this._uniqColorValues.indexOf(value);
                    return arg2_1[colorIndex % arg2_1.length];
                };
            }
            else if (lodash.isFunction(arg2_1)) {
                this.color = function (value, dataIndex, seriesModel) {
                    var ret = arg2_1(value, dataIndex, seriesModel);
                    if (!ret) {
                        return _this._defaultColorEncoder(value, dataIndex, seriesModel);
                    }
                    return ret;
                };
            }
            else if (lodash.isString(arg2_1)) {
                var originColors = arg2_1.split('-');
                var scale = new ColorScale();
                var minValue = this.getDataset().min(field);
                var maxValue = this.getDataset().max(field);
                var min = this._getScaleExtreme(minValue, 'min', scaleParam);
                var max = this._getScaleExtreme(maxValue, 'max', scaleParam);
                scale.colors(originColors);
                scale.domain([min, max]);
                this.color = scale.scale.bind(scale);
            }
        }
    };
    Encode.prototype._getScaleExtreme = function (minValue, key, scaleParam) {
        if (scaleParam && lodash.isNumber(scaleParam[key])) {
            return scaleParam[key];
        }
        return minValue;
    };
    Encode.prototype._initShape = function (shape) {
        if (shape) {
            if (typeof shape === 'string' || typeof shape === 'function') {
                this.shape = function () {
                    return shape;
                };
                return;
            }
            if (Array.isArray(shape) && shape.length === 2) {
                if (typeof shape[1] === 'function') {
                    this.shape = function (value, dataIndex, seriesModel) {
                        return shape[1](value, dataIndex, seriesModel);
                    };
                    return;
                }
                if (Array.isArray(shape[1]) && shape[1].length > 0) {
                    this.shape = function (value, dataIndex) {
                        return shape[1][dataIndex % shape[1].length];
                    };
                    return;
                }
            }
            this.shape = function () { return null; };
        }
    };
    Encode.prototype._initValueField = function (code, codeOption) {
        var _a, _b, _c;
        if (lodash.isNumber(codeOption)) {
            lodash.assign(this, (_a = {},
                _a[code.toString()] = function () { return codeOption; },
                _a));
        }
        else if (lodash.isArray(codeOption)) {
            var argArr = codeOption;
            var field = argArr[0];
            var arg2 = argArr[1]; // color array
            var scaleParam = argArr[2];
            if (lodash.isFunction(arg2)) {
                lodash.assign(this, (_b = {},
                    _b[code.toString()] = arg2,
                    _b));
            }
            else if (lodash.isArray(arg2)) {
                var _d = arg2, min = _d[0], max = _d[1];
                var maxValue = this.getDataset().max(field);
                var minValue = this.getDataset().min(field);
                var scaleMin = this._getScaleExtreme(minValue, 'min', scaleParam);
                var scaleMax = this._getScaleExtreme(maxValue, 'max', scaleParam);
                var valueScale_1 = new LinearScale(scaleMin, scaleMax, min, max);
                lodash.assign(this, (_c = {},
                    _c[code.toString()] = function (value) { return valueScale_1.scale(value); },
                    _c));
            }
        }
    };
    return Encode;
}());
export default Encode;
